<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'

import { useImageErrorHandling, useImageGallery } from '@/helpers/imageComposables'
import { useScrollEnd } from '@/helpers/scrollComposables'

import type { GalleryImage } from '@/types/images'

interface Props {
  images: GalleryImage[]
}

const props = defineProps<Props>()

const activeImageIndex = ref(0)
const carouselContainerRef = ref<HTMLElement | null>(null)
/** Flag to track if scrolling is happening programmatically (e.g. when using arrow keys or clicking the dots) */
const isGoingToImage = ref(false)

const { validImages, handleImageError } = useImageErrorHandling(() => props.images, {
  onError: () => {
    // If current image fails and there are other images, move to the next valid one
    if (validImages.value.length > 0 && activeImageIndex.value >= validImages.value.length) {
      activeImageIndex.value = Math.min(activeImageIndex.value, validImages.value.length - 1)
    }
  },
})

const { hasMultipleImages, noCroppingAllowed } = useImageGallery(validImages)

// For infinite loop: clone first and last images for seamless wrapping
const extendedImages = computed<(GalleryImage & { uniqueKey: string })[]>(() => {
  const wrapped = validImages.value.map((image, index) => ({
    ...image,
    uniqueKey: `original-${index}`,
  }))

  if (!hasMultipleImages.value) return wrapped

  return [
    // Use an empty string for alt text on cloned images so screen readers don't read them twice.
    { ...validImages.value[validImages.value.length - 1], alt: '', uniqueKey: 'clone-last' },
    ...wrapped,
    { ...validImages.value[0], alt: '', uniqueKey: 'clone-first' },
  ]
})

// ===== Scroll Helpers =====
const scrollToActiveImage = (behavior: 'smooth' | 'instant' = 'smooth') => {
  if (!carouselContainerRef.value || !hasMultipleImages.value) return

  const positionIncludingClones = activeImageIndex.value + 1 // account for clone at start
  const slide = carouselContainerRef.value.children[positionIncludingClones] as HTMLElement
  if (!slide) return

  carouselContainerRef.value.scrollTo({
    left: slide.offsetLeft,
    behavior: behavior,
  })
}

/**
 * Calculates which image from the original array is currently active based on scroll position.
 *
 * 1. Divides the scroll's x position by image width to determine which image in `extendedImages` is currently active.
 *    - round the value so that once an image has >50% of itself scrolled into the viewport it will count as active
 * 2. Maps that index for `extendedImages` to the index for `validImages` by accounting for the cloned images at the start and end.
 *
 * This works in tandem with `handleScrollEnd()` which instantly jumps to the real image
 * when a clone is detected, creating the illusion of infinite looping.
 *
 * @returns The 0-based index of the currently visible image in the `validImages` array
 */
const calculateActiveImage = (): number => {
  if (!hasMultipleImages.value || !carouselContainerRef.value) return 0

  const { scrollLeft, offsetWidth } = carouselContainerRef.value
  const position = Math.round(scrollLeft / offsetWidth)

  if (position === 0) return validImages.value.length - 1 // clone-last
  if (position > validImages.value.length) return 0 // clone-first
  return position - 1 // normal image
}

// ===== Scroll Event Handlers =====

const { handleScrollEnd, scheduleScrollEndFallback, clearScrollEndFallback } = useScrollEnd(() => {
  isGoingToImage.value = false
  // Jump to the real image when user scrolls to a clone
  nextTick(() => scrollToActiveImage('instant'))
})

const onScroll = () => {
  // Skip updating during programmatic scrolling to avoid race conditions
  if (isGoingToImage.value) return
  activeImageIndex.value = calculateActiveImage()

  // Schedule fallback for user-initiated scrolls in browsers without scrollend support.
  // Gets rescheduled on each scroll event (debounce pattern).
  scheduleScrollEndFallback()
}

// ===== Lifecycle & Watchers =====

onMounted(() => {
  nextTick(() => scrollToActiveImage('instant'))
})

// Re-calculate activeImageIndex when images fail to lazy load
watch(
  () => validImages.value.length,
  (newLength, oldLength) => {
    // Cancel any ongoing scroll to prevent race conditions
    if (carouselContainerRef.value) {
      const currentLeft = carouselContainerRef.value.scrollLeft
      carouselContainerRef.value.scrollTo({ left: currentLeft, behavior: 'instant' })
    }

    // If images were removed (e.g., failed to load), maintain position
    if (newLength > 0 && newLength < oldLength) {
      // Clamp activeImageIndex to valid range if it's now out of bounds
      if (activeImageIndex.value >= newLength) {
        activeImageIndex.value = newLength - 1
      }
      // Re-scroll to current position
      nextTick(() => scrollToActiveImage('instant'))
    }
  },
)

// ===== Navigation Functions =====

const goToImage = (imageIndex: number, behavior: 'smooth' | 'instant' = 'smooth') => {
  activeImageIndex.value = imageIndex
  isGoingToImage.value = true
  clearScrollEndFallback()
  scrollToActiveImage(behavior)
  if (behavior === 'instant') {
    // For instant scrolling, trigger scroll end immediately since scrollend may not fire
    nextTick(() => {
      handleScrollEnd()
    })
  } else {
    // For smooth scrolling, schedule fallback in case scrollend event is not supported
    scheduleScrollEndFallback()
  }
}

// Doesn't handle smooth wrapping (it jumps instantly when wrapping).
const navigate = (direction: -1 | 1) => {
  // Handles wrapping around the index correctly for negative and overflow values
  // Examples with 5 images:
  // -1 % 5 = -1, -1 + 5 = 4, 4 % 5 = 4
  // 5 % 5 = 0, 0 + 5 = 5, 5 % 5 = 0
  const index = activeImageIndex.value + direction
  const total = validImages.value.length
  const imageIndex = ((index % total) + total) % total
  // This is so it doesn't scroll through all the images when wrapping.
  const behavior = index !== imageIndex ? 'instant' : 'smooth'
  goToImage(imageIndex, behavior)
}

const handleKeyDown = (e: KeyboardEvent) => {
  if (!hasMultipleImages.value) return

  switch (e.key) {
    case 'ArrowLeft':
      e.preventDefault()
      navigate(-1)
      break
    case 'ArrowRight':
      e.preventDefault()
      navigate(1)
      break
  }
}
</script>

<template>
  <div v-if="validImages.length > 0" class="image-carousel" :class="{ 'no-crop': noCroppingAllowed }">
    <div
      ref="carouselContainerRef"
      :aria-label="hasMultipleImages ? 'Image carousel' : undefined"
      class="carousel-container"
      :role="hasMultipleImages ? 'region' : undefined"
      :tabindex="hasMultipleImages ? 0 : undefined"
      @keydown="handleKeyDown"
      @scroll="onScroll"
      @scrollend="handleScrollEnd"
    >
      <div v-for="image in extendedImages" :key="image.uniqueKey" class="carousel-slide">
        <img :alt="image.alt" loading="lazy" :src="image.src" @error="handleImageError(image)" />
      </div>
    </div>

    <div v-if="hasMultipleImages" aria-hidden="true" class="tap-zone tap-zone-prev" @click="navigate(-1)" />
    <div v-if="hasMultipleImages" aria-hidden="true" class="tap-zone tap-zone-next" @click="navigate(1)" />

    <div v-if="hasMultipleImages" aria-hidden="true" class="carousel-dots">
      <button
        v-for="(image, dotIndex) in validImages"
        :key="`dot-${image.src}`"
        class="dot"
        :class="{ active: dotIndex === activeImageIndex }"
        tabindex="-1"
        @click="goToImage(dotIndex)"
      />
    </div>
  </div>
</template>

<style scoped>
.dark-theme .image-carousel {
  --_dot-background-color: var(--black-a-500);
  --_dot-background-color-active: var(--black-a-950);
}
.image-carousel {
  /* Local Themable Variables */
  --_border-radius: var(--image-carousel-border-radius, var(--image-border-radius));
  --_background-color: var(--image-carousel-background-color, var(--image-background-color));

  --_dot-background-color: var(--white-a-500);
  --_dot-background-color-active: var(--white-a-950);

  position: relative;
  width: 100%;
  height: 300px;
  overflow: hidden;
  border-radius: var(--_border-radius);

  &.no-crop {
    border-radius: 0;
    height: 100%;

    .carousel-container {
      height: auto;

      .carousel-slide img {
        object-fit: contain;
      }
    }
  }

  .carousel-container {
    outline: none;

    display: flex;
    width: 100%;
    height: 100%;
    overflow-x: scroll;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;

    /* Hide scrollbar */
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE/Edge */

    &::-webkit-scrollbar {
      display: none; /* Chrome/Safari */
    }

    /* Prevent text selection during swipe */
    * {
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;
    }

    .carousel-slide {
      flex-shrink: 0;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      scroll-snap-align: start;
      scroll-snap-stop: always;
      background-color: var(--_background-color);

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        user-select: none;
        -webkit-user-drag: none;
        pointer-events: none;
      }
    }
  }

  /* Tap zones overlaid on top of the carousel */
  .tap-zone {
    display: none;

    /* Only enabled on devices with a precise pointer (mouse). 
    Touchscreen users navigate by swiping. */
    @media (hover: hover) and (pointer: fine) {
      display: block;
    }

    position: absolute;
    top: 0;
    bottom: 0;
    z-index: 1;
    cursor: pointer;

    &.tap-zone-prev {
      left: 0;
      width: 40%;
    }

    &.tap-zone-next {
      right: 0;
      width: 60%;
    }
  }

  .carousel-dots {
    position: absolute;
    bottom: var(--space-0-75, 16px);
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: var(--space-0-25, 4px);
    z-index: 2;

    .dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background-color: var(--_dot-background-color);
      border: none;
      padding: 0;
      cursor: pointer;
      transition: all var(--transition-duration) ease-in-out;

      &.active {
        background-color: var(--_dot-background-color-active);
      }
    }
  }
}
</style>
